import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ServicioInteropereabilidadService } from 'src/app/services/servicio-interopereabilidad.service';

const PDF_VIEWER_SCRIPT_PATH = 'assets/pdf.min.js';
const PDF_WORKER_PATH = 'assets/pdf.worker.min.js';

@Component({
  selector: 'app-reporte-interopereabilidad-hc',
  templateUrl: './reporte-interopereabilidad-hc.component.html',
  styleUrls: ['./reporte-interopereabilidad-hc.component.css']
})
export class ReporteInteropereabilidadHcComponent implements OnInit, OnDestroy {

  @ViewChild('documentDialogGuard') documentDialogGuard?: ElementRef;
  @ViewChild('documentPdfCanvasContainer') documentPdfCanvasContainer?: ElementRef;

  patient: PatientEntity = {} as PatientEntity;
  digitalSummaryAttention: DigitalSummaryAttention = {} as DigitalSummaryAttention;
  healthCareHistory: DigitalSummaryAttention = {} as DigitalSummaryAttention;
  historyTimelineCompositions: Composition[] = [];
  encounterCompositions: Composition[] = [];
  filteredCompositions: Composition[] = [];
  selectedComposition: Bundle = {} as Bundle;
  documentResource: DocumentReference = {} as DocumentReference;
  clinicalHistoryLoading: boolean = false;
  clinicalHistoryError: string | null = null;
  selectedCompositionLoading: boolean = false;
  selectedCompositionError: string | null = null;
  documentDialogTitle: string = 'Documento clinico';
  documentDialogIsPdf: boolean = false;
  documentDialogLoading: boolean = false;
  documentDialogError: string | null = null;
  documentDialogUrl: string | null = null;
  documentDialogViewerUrl: string | null = null;
  documentDialogVisible: boolean = false;
  private documentDialogRenderToken: number = 0;
  private activePdfLoadingTask: any = null;
  private activePdfDocument: any = null;
  private pdfViewerReadyPromise: Promise<any> | null = null;
  private selectedCompositionRequestToken: number = 0;
  encounterSearchTerm: string = '';
  numeroDocumento: string = '';
  parametros: ParametrosIntegracionEntity = {
    codigoBaseDatos: '',
    uncoSede: '',
    numeroDoc: '',
    tipoDoc: ''
  };

  constructor(private activatedRoute: ActivatedRoute,
    private servicioInteropereabilidad: ServicioInteropereabilidadService) {
    this.activatedRoute.params.subscribe(params => {
      this.parametros = {
        codigoBaseDatos: params['codigoBaseDatos'],
        uncoSede: params['uncoSede'],
        numeroDoc: params['numeroDoc'],
        tipoDoc: params['tipoDoc']
      };
    });

    this.numeroDocumento = this.parametros.numeroDoc;
    this.consultarDatos();
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.cleanupPdfResources();
    this.cleanupDocumentDialogUrl();
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent): void {
    if (!this.documentDialogVisible) {
      return;
    }

    const key = (event.key || '').toLowerCase();

    if ((event.ctrlKey || event.metaKey) && (key === 'p' || key === 's')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @HostListener('document:contextmenu', ['$event'])
  onDocumentContextMenu(event: MouseEvent): void {
    if (!this.documentDialogVisible) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  get selectedCompositionSummary(): Composition | undefined {
    const selectedCompositionId = this.selectedComposition && this.selectedComposition.compositionId;
    const composition = this.findCompositionById(selectedCompositionId);

    if (!selectedCompositionId || !composition) {
      return undefined;
    }

    return composition;
  }

  get hasEncounterSearchTerm(): boolean {
    return this.normalizeEncounterSearchTerm(this.encounterSearchTerm).length > 0;
  }

  get historyPreviewCompositions(): Composition[] {
    return this.historyTimelineCompositions.slice(0, 6);
  }

  get historyRemainingCompositions(): number {
    return Math.max(this.historyTimelineCompositions.length - this.historyPreviewCompositions.length, 0);
  }

  get historyLatestEncounter(): Composition | undefined {
    return this.historyTimelineCompositions.length ? this.historyTimelineCompositions[0] : undefined;
  }

  get historyOldestEncounter(): Composition | undefined {
    return this.historyTimelineCompositions.length
      ? this.historyTimelineCompositions[this.historyTimelineCompositions.length - 1]
      : undefined;
  }

  get historyUniqueModalitiesCount(): number {
    return this.countDistinctCompositionValues('modalidadAtencion', this.historyTimelineCompositions);
  }

  get historyUniqueServiceGroupsCount(): number {
    return this.countDistinctCompositionValues('grupoServicios', this.historyTimelineCompositions);
  }

  get selectedCompositionHistoryPosition(): number {
    const selectedCompositionId = this.selectedComposition && this.selectedComposition.compositionId;

    if (!selectedCompositionId) {
      return 0;
    }

    return this.historyTimelineCompositions.findIndex((composition: Composition) => composition.id === selectedCompositionId) + 1;
  }

  get encounterDate(): Date | undefined {
    const encounter = this.selectedComposition && this.selectedComposition.encuentro;
    return encounter && encounter.periodoInicio ? encounter.periodoInicio : this.selectedCompositionSummary && this.selectedCompositionSummary.date;
  }

  get encounterEndDate(): Date | undefined {
    const encounter = this.selectedComposition && this.selectedComposition.encuentro;
    return encounter && encounter.periodoFin ? encounter.periodoFin : this.selectedCompositionSummary && this.selectedCompositionSummary.fechaFinAtencion;
  }

  get professionalName(): string {
    const profesional = this.selectedComposition && this.selectedComposition.profesional;

    if (!profesional) {
      return '—';
    }

    if (profesional.nombreCompleto) {
      return profesional.nombreCompleto;
    }

    return [profesional.primerNombre, profesional.primerApellido, profesional.segundoApellido]
      .filter((value: string) => !!value)
      .join(' ') || '—';
  }

  get incapacitySummary(): string {
    const incapacidad = this.selectedComposition && this.selectedComposition.incapacidad;
    const parts: string[] = [];

    if (!incapacidad) {
      return '—';
    }

    if (incapacidad.diasIncapacidad !== undefined && incapacidad.diasIncapacidad !== null) {
      parts.push((incapacidad.diasIncapacidad + ' ' + (incapacidad.diasUnidad || '')).trim());
    }

    if (incapacidad.alcanceDisplay) {
      parts.push(incapacidad.alcanceDisplay);
    }

    return parts.join(' · ') || '—';
  }

  getDiagnosisClassification(diagnostico: Diagnostico): string {
    const primaryDiagnosisId = this.getEncounterPrimaryDiagnosisId();
    const diagnosisId = this.normalizeResourceReference(diagnostico && diagnostico.id);

    if (!primaryDiagnosisId || !diagnosisId) {
      return '';
    }

    return primaryDiagnosisId === diagnosisId ? 'primary' : 'secondary';
  }

  getEncounterDiagnosisTypeLabel(): string {
    const encounter = this.selectedComposition && this.selectedComposition.encuentro;

    if (!encounter) {
      return '';
    }

    const diagnosisTypeCode = (encounter.tipoDiagnosticoCode || '').trim();
    const diagnosisTypeDisplay = (encounter.tipoDiagnosticoDisplay || '').trim();

    if (diagnosisTypeCode && diagnosisTypeDisplay) {
      return diagnosisTypeCode + ' - ' + diagnosisTypeDisplay;
    }

    return diagnosisTypeCode || diagnosisTypeDisplay;
  }

  formatMedicationPlan(medicamento: Medicamento): string {
    const parts: string[] = [];

    if (medicamento.dosisValor !== undefined && medicamento.dosisValor !== null) {
      parts.push((medicamento.dosisValor + ' ' + (medicamento.dosisUnidad || '')).trim());
    }

    if (medicamento.frecuenciaValor !== undefined && medicamento.frecuenciaValor !== null) {
      parts.push((medicamento.frecuenciaValor + ' ' + (medicamento.frecuenciaUnidad || '')).trim());
    }

    if (medicamento.duracionValor !== undefined && medicamento.duracionValor !== null) {
      parts.push((medicamento.duracionValor + ' ' + (medicamento.duracionUnidad || '')).trim());
    }

    return parts.join(' / ') || '—';
  }

  getTechnologyLabel(recurso: OtrasTecnologia): string {
    return recurso.nombreServicio || recurso.descripcion || '—';
  }

  getTechnologyCode(recurso: OtrasTecnologia): string {
    return recurso.codigoServicio || '—';
  }

  getDocumentType(documento: Documento): string {
    return documento.tipoColombianDisplay || documento.tipoLoincDisplay || '—';
  }

  getFamilyHistoryRelationship(antecedente: AntecedenteFamiliar): string {
    return this.resolveFirstValue(antecedente, [
      'parentescoDisplay',
      'parentescoCode',
      'relationship.text',
      'relationship.coding.0.display',
      'familiar',
      'parentesco',
      'tipoParentesco',
      'display'
    ]);
  }

  getFamilyHistoryConditionCode(antecedente: AntecedenteFamiliar): string {
    return this.resolveFirstValue(antecedente, [
      'condicionCode',
      'codigoCie10',
      'conditionCode',
      'condition.0.code.coding.0.code',
      'codigo',
      'code'
    ]);
  }

  getFamilyHistoryCondition(antecedente: AntecedenteFamiliar): string {
    return this.resolveFirstValue(antecedente, [
      'condicionDisplay',
      'descripcion',
      'conditionDisplay',
      'condition.0.code.coding.0.display',
      'condition.0.code.text',
      'texto',
      'diagnostico',
      'condicion'
    ]);
  }

  getFamilyHistoryStatus(antecedente: AntecedenteFamiliar): string {
    return this.resolveFirstValue(antecedente, [
      'estado',
      'status',
      'clinicalStatus',
      'estadoClinico'
    ]);
  }

  private resolveFirstValue(source: any, paths: string[]): string {
    let index = 0;

    while (index < paths.length) {
      const value = this.readPath(source, paths[index]);

      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value);
      }

      index += 1;
    }

    return '—';
  }

  private readPath(source: any, path: string): any {
    return path.split('.').reduce((current: any, segment: string) => {
      if (current === undefined || current === null) {
        return undefined;
      }

      if (/^\d+$/.test(segment)) {
        return current[Number(segment)];
      }

      return current[segment];
    }, source);
  }

  private getEncounterPrimaryDiagnosisId(): string {
    return this.normalizeResourceReference(
      this.selectedComposition
      && this.selectedComposition.encuentro
      && this.selectedComposition.encuentro.diagnosticoPrincipalRef
    );
  }

  private normalizeResourceReference(reference: string | null | undefined): string {
    if (!reference) {
      return '';
    }

    const normalizedReference = String(reference).trim().replace(/^#/, '');
    const referenceWithoutHistory = normalizedReference.split('/_history/')[0];

    if (!referenceWithoutHistory) {
      return '';
    }

    return referenceWithoutHistory.split('/').filter((segment: string) => !!segment).pop() || referenceWithoutHistory;
  }

  onPreventDocumentAction(event: any): void {
    if (!this.documentDialogVisible) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  onDocumentDialogHide(): void {
    this.documentDialogVisible = false;
    this.documentDialogIsPdf = false;
    this.documentDialogLoading = false;
    this.documentDialogError = null;
    this.documentDialogRenderToken += 1;
    this.cleanupPdfResources();
    this.cleanupDocumentDialogUrl();
  }

  private openDocumentDialog(base64Data: string, mimeType: string, title: string): void {
    const normalizedMimeType = mimeType || 'application/pdf';
    const renderToken = this.documentDialogRenderToken + 1;

    this.documentDialogRenderToken = renderToken;
    this.documentDialogTitle = title || 'Documento clinico';
    this.documentDialogIsPdf = this.isPdfMimeType(normalizedMimeType);
    this.documentDialogLoading = true;
    this.documentDialogError = null;
    this.cleanupPdfResources();
    this.cleanupDocumentDialogUrl();
    this.documentDialogVisible = true;

    if (this.documentDialogIsPdf) {
      setTimeout(() => {
        this.focusDocumentDialog();
        void this.renderPdfDocument(base64Data, renderToken);
      });
      return;
    }

    this.documentDialogUrl = this.createDocumentDialogUrl(base64Data, normalizedMimeType);
    this.documentDialogViewerUrl = this.buildDocumentViewerUrl(this.documentDialogUrl, normalizedMimeType);
    this.documentDialogLoading = false;

    setTimeout(() => {
      this.focusDocumentDialog();
    });
  }

  private async renderPdfDocument(base64Data: string, renderToken: number): Promise<void> {
    const canvasContainer = this.documentPdfCanvasContainer && this.documentPdfCanvasContainer.nativeElement;

    if (!canvasContainer) {
      if (renderToken === this.documentDialogRenderToken) {
        this.documentDialogError = 'No fue posible inicializar el visor protegido del documento.';
        this.documentDialogLoading = false;
      }

      return;
    }

    this.clearPdfCanvasContainer();

    try {
      const pdfViewerLibrary = await this.ensurePdfViewerLibrary();

      if (renderToken !== this.documentDialogRenderToken || !this.documentDialogVisible) {
        return;
      }

      this.activePdfLoadingTask = pdfViewerLibrary.getDocument({
        data: this.createDocumentBytes(base64Data),
        disableAutoFetch: true,
        disableStream: true,
        isEvalSupported: false
      });

      const pdfDocument = await this.activePdfLoadingTask.promise;

      if (renderToken !== this.documentDialogRenderToken || !this.documentDialogVisible) {
        if (pdfDocument && pdfDocument.destroy) {
          pdfDocument.destroy();
        }

        return;
      }

      this.activePdfDocument = pdfDocument;

      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        const page = await pdfDocument.getPage(pageNumber);

        if (renderToken !== this.documentDialogRenderToken || !this.documentDialogVisible) {
          if (page && page.cleanup) {
            page.cleanup();
          }

          return;
        }

        const baseViewport = page.getViewport({ scale: 1 });
        const devicePixelRatio = window.devicePixelRatio || 1;
        const availableWidth = Math.max((canvasContainer.clientWidth || 900) - 32, 320);
        const cssScale = Math.min(Math.max(availableWidth / baseViewport.width, 1), 2);
        const viewport = page.getViewport({ scale: cssScale * devicePixelRatio });
        const wrapper = document.createElement('div');
        const pageLabel = document.createElement('div');
        const canvas = document.createElement('canvas');
        const canvasContext = canvas.getContext('2d');

        wrapper.className = 'iop-document-page';
        pageLabel.className = 'iop-document-page-label';
        pageLabel.textContent = 'Pagina ' + pageNumber + ' de ' + pdfDocument.numPages;
        canvas.className = 'iop-document-canvas';
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = Math.floor(viewport.width / devicePixelRatio) + 'px';
        canvas.style.height = Math.floor(viewport.height / devicePixelRatio) + 'px';
        canvas.setAttribute('aria-label', 'Pagina ' + pageNumber + ' del documento PDF');
        wrapper.appendChild(pageLabel);
        wrapper.appendChild(canvas);
        canvasContainer.appendChild(wrapper);

        if (!canvasContext) {
          continue;
        }

        await page.render({
          canvasContext,
          viewport
        }).promise;

        if (page.cleanup) {
          page.cleanup();
        }
      }

      if (renderToken === this.documentDialogRenderToken) {
        this.documentDialogLoading = false;
      }
    } catch (error) {
      if (renderToken === this.documentDialogRenderToken) {
        console.error('Error al renderizar el PDF protegido:', error);
        this.documentDialogError = 'No fue posible visualizar el PDF en modo protegido.';
        this.documentDialogLoading = false;
      }
    }
  }

  private focusDocumentDialog(): void {
    if (!this.documentDialogVisible || !this.documentDialogGuard || !this.documentDialogGuard.nativeElement) {
      return;
    }

    this.documentDialogGuard.nativeElement.focus();
  }

  private ensurePdfViewerLibrary(): Promise<any> {
    const browserWindow = window as any;

    if (browserWindow.pdfjsLib) {
      browserWindow.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_PATH;
      return Promise.resolve(browserWindow.pdfjsLib);
    }

    if (this.pdfViewerReadyPromise) {
      return this.pdfViewerReadyPromise;
    }

    this.pdfViewerReadyPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = PDF_VIEWER_SCRIPT_PATH;
      script.async = true;
      script.onload = () => {
        const loadedPdfViewerLibrary = (window as any).pdfjsLib;

        if (!loadedPdfViewerLibrary) {
          this.pdfViewerReadyPromise = null;
          reject(new Error('No se pudo inicializar la libreria PDF.'));
          return;
        }

        loadedPdfViewerLibrary.GlobalWorkerOptions.workerSrc = PDF_WORKER_PATH;
        resolve(loadedPdfViewerLibrary);
      };
      script.onerror = () => {
        this.pdfViewerReadyPromise = null;
        reject(new Error('No se pudo cargar la libreria PDF.'));
      };

      document.body.appendChild(script);
    });

    return this.pdfViewerReadyPromise;
  }

  private isPdfMimeType(mimeType: string): boolean {
    return (mimeType || '').toLowerCase().indexOf('pdf') !== -1;
  }

  private buildDocumentViewerUrl(documentUrl: string, mimeType: string): string {
    if (this.isPdfMimeType(mimeType)) {
      return documentUrl + '#toolbar=0&navpanes=0&scrollbar=1&statusbar=0&messages=0&view=FitH&pagemode=none';
    }

    if ((mimeType || '').toLowerCase() === '') {
      return documentUrl;
    }

    return documentUrl;
  }

  private createDocumentBytes(base64Data: string): Uint8Array {
    const normalizedBase64 = (base64Data.split(',').pop() || '').replace(/\s/g, '');
    const byteCharacters = atob(normalizedBase64);
    const byteBuffer = new ArrayBuffer(byteCharacters.length);
    const byteArray = new Uint8Array(byteBuffer);

    for (let index = 0; index < byteCharacters.length; index += 1) {
      byteArray[index] = byteCharacters.charCodeAt(index);
    }

    return byteArray;
  }

  private createDocumentDialogUrl(base64Data: string, mimeType: string): string {
    const documentBytes = this.createDocumentBytes(base64Data);

    return URL.createObjectURL(new Blob([documentBytes.buffer as ArrayBuffer], {
      type: mimeType || 'application/pdf'
    }));
  }

  private clearPdfCanvasContainer(): void {
    if (!this.documentPdfCanvasContainer || !this.documentPdfCanvasContainer.nativeElement) {
      return;
    }

    this.documentPdfCanvasContainer.nativeElement.innerHTML = '';
  }

  private cleanupPdfResources(): void {
    const loadingTask = this.activePdfLoadingTask;
    const pdfDocument = this.activePdfDocument;

    this.activePdfLoadingTask = null;
    this.activePdfDocument = null;

    if (loadingTask && loadingTask.destroy) {
      try {
        loadingTask.destroy();
      } catch (error) {
        console.warn('No fue posible liberar la tarea del visor PDF:', error);
      }
    }

    if (pdfDocument) {
      try {
        if (pdfDocument.cleanup) {
          pdfDocument.cleanup();
        }

        if (pdfDocument.destroy) {
          pdfDocument.destroy();
        }
      } catch (error) {
        console.warn('No fue posible liberar el documento PDF:', error);
      }
    }

    this.clearPdfCanvasContainer();
  }

  private cleanupDocumentDialogUrl(): void {
    if (!this.documentDialogUrl) {
      this.documentDialogViewerUrl = null;
      return;
    }

    URL.revokeObjectURL(this.documentDialogUrl);
    this.documentDialogUrl = null;
    this.documentDialogViewerUrl = null;
  }

  onConsultarInformacion(numeroDoc: string): void {
    this.numeroDocumento = numeroDoc;

    this.consultarDatos();
  }

  onEncounterSearch(searchTerm: string): void {
    this.encounterSearchTerm = searchTerm || '';
    this.applyEncounterSearch();
  }

  clearEncounterSearch(): void {
    if (!this.encounterSearchTerm) {
      return;
    }

    this.encounterSearchTerm = '';
    this.applyEncounterSearch();
  }

  consultarDatos(): void {
    this.encounterSearchTerm = '';
    this.patient = {} as PatientEntity;
    this.healthCareHistory = {} as DigitalSummaryAttention;
    this.digitalSummaryAttention = {} as DigitalSummaryAttention;
    this.historyTimelineCompositions = [];
    this.encounterCompositions = [];
    this.filteredCompositions = [];
    this.selectedComposition = {} as Bundle;
    this.clinicalHistoryLoading = true;
    this.clinicalHistoryError = null;
    this.selectedCompositionLoading = false;
    this.selectedCompositionError = null;

    forkJoin([
      this.servicioInteropereabilidad.getEventos(
        this.parametros.codigoBaseDatos,
        this.parametros.uncoSede,
        this.numeroDocumento,
        this.parametros.tipoDoc
      ).pipe(catchError((err: any) => {
        console.error('Error al obtener eventos:', err);
        return of(null);
      })),
      this.servicioInteropereabilidad.getConsultarRdaPaciente(
        this.parametros.codigoBaseDatos,
        this.parametros.uncoSede,
        this.numeroDocumento,
        this.parametros.tipoDoc
      ).pipe(catchError((err: any) => {
        console.error('Error al obtener RDA:', err);
        return of(null);
      })),
      this.servicioInteropereabilidad.getEncuentrosClinicos(
        this.parametros.codigoBaseDatos,
        this.parametros.uncoSede,
        this.numeroDocumento,
        this.parametros.tipoDoc
      ).pipe(catchError((err: any) => {
        console.error('Error al obtener encuentros clínicos:', err);
        return of(null);
      }))
    ]).subscribe(([eventos, rda, encuentrosClinicos]) => {
      this.patient = eventos ? eventos as PatientEntity : {} as PatientEntity;
      this.healthCareHistory = rda ? rda as DigitalSummaryAttention : {} as DigitalSummaryAttention;
      this.digitalSummaryAttention = encuentrosClinicos ? encuentrosClinicos as DigitalSummaryAttention : {} as DigitalSummaryAttention;
      this.historyTimelineCompositions = this.sortCompositionsByDate(
        this.resolveCompositionSource(this.healthCareHistory, this.digitalSummaryAttention)
      );
      this.encounterCompositions = this.sortCompositionsByDate(
        this.resolveCompositionSource(this.digitalSummaryAttention, this.healthCareHistory)
      );
      this.applyEncounterSearch();

      if (!this.historyTimelineCompositions.length && !this.encounterCompositions.length) {
        this.clinicalHistoryError = 'No se encontraron atenciones interoperables para este paciente.';
      }

      this.clinicalHistoryLoading = false;
    }, (err: any) => {
      console.error('Error al obtener datos:', err);
      this.clinicalHistoryError = 'No fue posible cargar el historial de atenciones del paciente.';
      this.clinicalHistoryLoading = false;
    });
  }

  onSelectComposition(composition: Composition): void {
    const requestToken = this.selectedCompositionRequestToken + 1;

    if (!composition || !composition.id) {
      return;
    }

    this.selectedCompositionRequestToken = requestToken;
    this.selectedComposition = { compositionId: composition.id } as Bundle;
    this.selectedCompositionLoading = true;
    this.selectedCompositionError = null;

    this.servicioInteropereabilidad.getConsultarRdaByCompositionID(
      this.parametros.codigoBaseDatos,
      this.parametros.uncoSede,
      composition.id
    ).subscribe((resp: any) => {
      if (requestToken !== this.selectedCompositionRequestToken) {
        return;
      }

      this.selectedComposition = resp || ({ compositionId: composition.id } as Bundle);
      this.selectedCompositionLoading = false;
    }, (err: any) => {
      if (requestToken !== this.selectedCompositionRequestToken) {
        return;
      }

      console.error('Error al obtener RDA por ID de composición:', err);
      this.selectedCompositionLoading = false;
      this.selectedCompositionError = 'No fue posible cargar el detalle interoperable de la atención seleccionada.';
    });
  }

  onViewDocument(documento: Documento): void {
    if (!documento.attachmentUrl) {
      console.warn('El documento no tiene una URL de adjunto para visualizar.');
      return;
    }

    this.servicioInteropereabilidad.getDocumentResourceByDocumentId(
      this.parametros.codigoBaseDatos,
      this.parametros.uncoSede,
      documento.attachmentUrl
    ).subscribe((resp: any) => {
      this.documentResource = resp;

      const contentWithData = this.documentResource.content && this.documentResource.content.find((content: Content) => {
        return !!(content.attachment && content.attachment.data);
      });

      if (!contentWithData || !contentWithData.attachment || !contentWithData.attachment.data) {
        console.warn('El recurso del documento no contiene un PDF embebido en base64.');
        return;
      }

      this.openDocumentDialog(
        contentWithData.attachment.data,
        documento.formatoMime || 'application/pdf',
        documento.descripcion || this.getDocumentType(documento)
      );
    }, (err: any) => {
      console.error('Error al obtener el recurso del documento:', err);
    });
  }

  private applyEncounterSearch(): void {
    const compositions = this.encounterCompositions.slice();
    const normalizedSearchTerm = this.normalizeEncounterSearchTerm(this.encounterSearchTerm);

    if (!normalizedSearchTerm) {
      this.filteredCompositions = compositions.slice();
      return;
    }

    this.filteredCompositions = compositions.filter((composition: Composition) => {
      return this.matchesEncounterSearch(composition, normalizedSearchTerm);
    });
  }

  private matchesEncounterSearch(composition: Composition, normalizedSearchTerm: string): boolean {
    const searchableText = [
      composition.id,
      composition.title,
      composition.modalidadAtencion,
      composition.grupoServicios,
      composition.status,
      this.formatEncounterDateForSearch(composition.date),
      this.formatEncounterDateForSearch(composition.fechaInicioAtencion),
      this.formatEncounterDateForSearch(composition.fechaFinAtencion)
    ].join(' ');

    return this.normalizeEncounterSearchTerm(searchableText).indexOf(normalizedSearchTerm) !== -1;
  }

  private normalizeEncounterSearchTerm(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/ñ/g, 'n')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private formatEncounterDateForSearch(value: Date | string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return String(value);
    }

    const year = String(date.getFullYear());
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);

    return [
      year + '-' + month + '-' + day,
      day + '/' + month + '/' + year,
      day + '-' + month + '-' + year,
      date.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' }),
      date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
    ].join(' ');
  }

  private resolveCompositionSource(primary: DigitalSummaryAttention, fallback: DigitalSummaryAttention): Composition[] {
    if (primary && primary.compositions && primary.compositions.length) {
      return primary.compositions.slice();
    }

    if (fallback && fallback.compositions && fallback.compositions.length) {
      return fallback.compositions.slice();
    }

    return [];
  }

  private sortCompositionsByDate(compositions: Composition[]): Composition[] {
    return compositions.slice().sort((current: Composition, next: Composition) => {
      return this.getCompositionTimestamp(next) - this.getCompositionTimestamp(current);
    });
  }

  private getCompositionTimestamp(composition: Composition): number {
    const compositionDate = composition && (composition.date || composition.fechaInicioAtencion || composition.fechaFinAtencion);
    const parsedDate = compositionDate ? new Date(compositionDate) : null;

    return parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate.getTime() : 0;
  }

  private countDistinctCompositionValues(property: 'modalidadAtencion' | 'grupoServicios', compositions: Composition[]): number {
    return compositions.reduce((values: string[], composition: Composition) => {
      const currentValue = composition && composition[property] ? String(composition[property]).trim() : '';

      if (currentValue && values.indexOf(currentValue) === -1) {
        values.push(currentValue);
      }

      return values;
    }, []).length;
  }

  private findCompositionById(compositionId: string): Composition | undefined {
    if (!compositionId) {
      return undefined;
    }

    return this.encounterCompositions.find((composition: Composition) => composition.id === compositionId)
      || this.historyTimelineCompositions.find((composition: Composition) => composition.id === compositionId);
  }
}

interface ParametrosIntegracionEntity {
  codigoBaseDatos: string;
  uncoSede: string;
  numeroDoc: string;
  tipoDoc: string;
}

interface PatientEntity {
  resourceType: string;
  id: string;
  extension: GenderExtension[];
  identifier: Identifier[];
  active: boolean;
  name: Name[];
  gender: string;
  birthDate: Date;
  deceasedBoolean: boolean;
  address: Address[];
  horaNacimiento: string;
  zonaResidencia: string;
  identidadGenero: string;
  generoBiologico: string;
  numeroDocumento: string;
  primerApellido: string;
  segundoApellido: string;
  nombreCompleto: string;
  tipoDocumento: string;
  primerNombre: string;
  segundoNombre: string;
  nacionalidad: string;
  ciudad: string;
  discapacidad: string;
  etnia: string;
  _gender: Gender;
  _birthDate: BirthDate;
}

interface GenderExtension {
  url: string;
  valueCoding: Coding;
}

interface Coding {
  system: string;
  code: string;
  display: null | string;
}

interface Identifier {
  id: string;
  use: string;
  type: Type;
  system: string;
  value: string;
}

interface Type {
  coding: Coding[];
  tipoDocumento: string;
}

interface Name {
  use: string;
  family: string;
  given: string[];
  nombreCompleto: string;
  primerNombre: string;
  segundoNombre: string;
  _family: Family;
}

interface Family {
  extension: BirthDateExtension[];
  primerApellido: string;
  segundoApellido: string;
}

interface BirthDateExtension {
  url: string;
  valueString: null | string;
  valueTime: string | null;
}

interface BirthDate {
  extension: BirthDateExtension[];
}

interface Gender {
  extension: GenderExtension[];
}

interface Address {
  id: string;
  use: string;
  type: string;
  city: string;
  country: string;
  extension: GenderExtension[];
  zonaResidencia: string;
  codigoDivipola: null;
  _city: Gender;
  _country: Gender;
  text?:      string;
  state?:     string;
  _state?:    City;
}

// Resto de las interfaces para mapear la respuesta del paciente
interface DigitalSummaryAttention {
  resourceType: string;
  id: string;
  type: string;
  entry: Entry[];
  totalCompositions: number;
  compositions: Composition[];
}

interface Bundle {
    compositionId:          string;
    paciente:               Paciente;
    profesional:            Profesional;
    ipsPrestadora:          IPSPrestadora;
    eapb:                   Eapb;
    encuentro:              Encuentro;
    ocupacion:              Ocupacion;
    incapacidad:            Incapacidad;
    diagnosticos:           Diagnostico[];
    alergias:               Alergia[];
    antecedentesFamiliares: AntecedenteFamiliar[];
    factoresRiesgo:         Alergia[];
    medicamentos:           Medicamento[];
    procedimientos:         OtrasTecnologia[];
    otrasTecnologias:       OtrasTecnologia[];
    documentos:             Documento[];
}

interface AntecedenteFamiliar {
  id?: string;
  [key: string]: any;
}

interface Paciente {
    id:              string;
    tipoDocumento:   string;
    numeroDocumento: string;
    primerNombre:    string;
    segundoNombre:   string;
    primerApellido:  string;
    segundoApellido: string;
    nombreCompleto:  string;
    genero:          string;
    generoBiologico: string;
    identidadGenero: string;
    fechaNacimiento: Date;
    horaNacimiento:  string;
    fallecido:       boolean;
    nacionalidad:    string;
    etnia:           string;
    discapacidad:    string;
    ciudad:          string;
    codigoDivipola:  string;
    zonaResidencia:  string;
}

interface Profesional {
    id:              string;
    tipoDocumento:   string;
    numeroDocumento: string;
    nombreCompleto:  string;
    primerNombre:    string;
    primerApellido:  string;
    segundoApellido: string;
}

interface Alergia {
    id:             string;
    estadoClinico?: string;
    tipoCode:       string;
    tipoDisplay:    string;
    descripcion:    string;
    estado?:        string;
}

interface Encuentro {
    id:                      string;
    numeroCuenta:            string;
    estado:                  string;
    claseCode:               string;
    claseDisplay:            string;
    modalidadCode:           string;
    modalidadDisplay:        string;
    grupoServicioCode:       string;
    grupoServicioDisplay:    string;
    especialidadCode:        string;
    especialidadDisplay:     string;
    entornoCode:             string;
    entornoDisplay:          string;
    servicioCode:            string;
    servicioDisplay:         string;
    periodoInicio:           Date;
    periodoFin:              Date;
    causaExternaCode:        string;
    causaExternaDisplay:     string;
    tipoDiagnosticoCode:     string;
    tipoDiagnosticoDisplay:  string;
    diagnosticoPrincipalRef: string;
    egresoCode:              string;
    egresoDisplay:           string;
}

interface Ocupacion {
    id:               string;
    ocupacionCode:    string;
    ocupacionDisplay: string;
}

interface Incapacidad {
    id:              string;
    alcanceCode:     string;
    alcanceDisplay:  string;
    diasIncapacidad: number;
    diasUnidad:      string;
}

interface IPSPrestadora {
    id:                      string;
    nit:                     string;
    codigoPrestador:         string;
    nombre:                  string;
    ciudad:                  string;
    codigoDivipolaMunicipio: string;
}

interface Eapb {
    id:         string;
    codigoEapb: string;
    nombre:     string;
}

interface Diagnostico {
    id:                 string;
    estadoClinico:      string;
    estadoVerificacion: string;
    categoria:          string;
    codigoCie10:        string;
    descripcion:        string;
}

interface Medicamento {
    id:                       string;
    estado:                   string;
    categoriaCode:            string;
    categoriaDisplay:         string;
    medicamentoCode:          string;
    medicamentoDisplay:       string;
    fechaPrescripcion:        Date;
    finalidadCode:            string;
    finalidadDisplay:         string;
    viaAdministracionCode:    string;
    viaAdministracionDisplay: string;
    dosisValor:               number;
    dosisUnidad:              string;
    frecuenciaValor:          number;
    frecuenciaUnidad:         string;
    duracionValor:            number;
    duracionUnidad:           string;
}

interface OtrasTecnologia {
    id:                  string;
    estado:              string;
    categoriaCode:       string;
    categoriaDisplay:    string;
    fechaOrden:          Date;
    finalidadCode:       string;
    finalidadDisplay:    string;
    esProcedimientoCups: boolean;
    descripcion?:        string;
    codigoServicio?:     string;
    nombreServicio?:     string;
}

interface Documento {
    id:                   string;
    estado:               string;
    tipoLoincCode:        string;
    tipoLoincDisplay:     string;
    tipoColombianCode:    string;
    tipoColombianDisplay: string;
    fecha:                Date;
    descripcion:          string;
    confidencialidad:     string;
    attachmentUrl:        string;
    formatoMime:          string;
}

interface Composition {
    resourceType:        string;
    id:                  string;
    meta:                Meta;
    status:              string;
    type:                Type;
    subject:             Custodian;
    date:                Date;
    author:              Custodian[];
    title:               string;
    confidentiality:     string;
    attester:            Attester[];
    custodian:           Custodian;
    event:               Event[];
    section:             Section[];
    patientId:           string;
    authorId:            string;
    grupoServicios:      string;
    organizationId:      string;
    fechaInicioAtencion: Date;
    fechaFinAtencion:    Date;
    modalidadAtencion:   string;
}

interface Attester {
    mode:  string;
    party: Custodian;
}

interface Custodian {
    reference:    string;
    resourceType: string;
    resourceId:   string;
}

interface Event {
    code:           Type[];
    period:         Period;
    modalidad:      string;
    grupoServicios: string;
}

interface Type {
    coding:  Coding[];
    text:    null;
    code:    string;
    display: string;
}

interface Coding {
    system:  string;
    code:    string;
    display: string | null;
}

interface Period {
    start: Date;
    end:   Date;
}

interface Meta {
    versionId:   string;
    lastUpdated: Date;
    profile:     string[];
}

interface Section {
    title:       string;
    code:        Type;
    text?:        Text;
    emptyReason?: ClinicalStatus ;
    entry:       Custodian[];
    codigoLoinc: string;
}

interface Entry {
    fullUrl:  string;
    resource: Composition;
    search:   Search;
}

interface Search {
    mode: string;
}

interface EntryComposition {
    resource: Resource;
}

interface Resource {
    resourceType:        string;
    id:                  string;
    meta:                Meta;
    status?:             string;
    type?:               ClinicalStatus[] | ClinicalStatus;
    subject?:            Custodian;
    date?:               Date;
    author?:             Custodian[];
    title?:              string;
    confidentiality?:    string;
    attester?:           Attester[];
    custodian?:          Custodian;
    event?:              Event[];
    section?:            Section[];
    extension?:          GenderExtension[];
    identifier?:         ResourceIdentifier[];
    active?:             boolean;
    name?:               NameElement[] | string;
    gender?:             string;
    _gender?:            Gender;
    birthDate?:          Date;
    _birthDate?:         BirthDate;
    deceasedBoolean?:    boolean;
    address?:            Address[];
    qualification?:      Qualification[];
    clinicalStatus?:     ClinicalStatus;
    verificationStatus?: VerificationStatus;
    category?:           ClinicalStatus[];
    code?:               Code;
    patient?:            Custodian;
    relationship?:       ClinicalStatus;
    condition?:          Condition[];
}

interface City {
    extension: CityExtension[];
}

interface CityExtension {
    url:         string;
    valueCoding: ValueCoding;
}

interface ValueCoding {
    system: string;
    code:   string;
}

interface Attester {
    mode:  string;
    party: Custodian;
}

interface Custodian {
    reference: string;
}

interface ClinicalStatus {
    coding: ValueCodingElement[];
}

interface Code {
    coding: ValueCodingElement[];
    text:   string;
}

interface Condition {
    code: ClinicalStatus;
}

interface EventPeriod {
    start: Date;
    end:   Date;
}

interface ResourceIdentifier {
    id:      string;
    use:     string;
    type:    ClinicalStatus;
    system?: string;
    value:   string;
    period?: IdentifierPeriod;
}

interface IdentifierPeriod {
    start: Date;
}

interface Meta {
    versionId:   string;
    lastUpdated: Date;
    profile:     string[];
}

interface NameElement {
    use:     string;
    family:  string;
    _family: Family;
    given:   string[];
}

interface Qualification {
    identifier: QualificationIdentifier[];
    code:       ClinicalStatus;
    period:     IdentifierPeriod;
}

interface QualificationIdentifier {
    use:      string;
    type:     ClinicalStatus;
    system:   string;
    value:    string;
    assigner: Assigner;
}

interface Assigner {
    type:    string;
    display: string;
}

interface Text {
    status: string;
    div:    string;
}

interface VerificationStatus {
    coding: VerificationStatusCoding[];
}

interface VerificationStatusCoding {
    code:    string;
    display: string;
}

interface ValueCodingElement {
    system?: string;
    code:    string;
    display: string;
}

// Document reference
interface DocumentReference {
    resourceType:  string;
    id:            string;
    meta:          Meta;
    text:          Text;
    status:        string;
    type:          Type;
    category:      Type[];
    subject:       Custodian;
    date:          Date;
    author:        Custodian[];
    custodian:     Custodian;
    description:   string;
    securityLabel: Type[];
    context:       Context;
    content:       Content[];
}

interface Context {
    encounter: Custodian[];
}

interface Content {
    attachment: Attachment;
    format:     Format;
}

interface Attachment {
    url:  string;
    data: string;
}

interface Format {
    system:  string;
    code:    string;
    display: string;
}